<script setup>
import { ref, onMounted } from 'vue';
import { Button } from '@/components/ui/button';
import { Check, Settings } from 'lucide-vue-next';
import { i18n } from '@/i18n';
const showGDPR = ref(false);
const showDetails = ref(false);

// Cookie preferences
const preferences = ref({
  necessary: true, // Always required
  analytics: false,
  marketing: false,
  functional: false,
});

onMounted(() => {
  // Check if user has already made a choice
  const gdprConsent = localStorage.getItem('gdpr-consent');
  if (!gdprConsent && !i18n.isChinese) {
    // showGDPR.value = true;
  }
});

const acceptAll = () => {
  preferences.value.analytics = true;
  preferences.value.marketing = true;
  preferences.value.functional = true;
  savePreferences();
};

const acceptNecessary = () => {
  preferences.value.analytics = false;
  preferences.value.marketing = false;
  preferences.value.functional = false;
  savePreferences();
};

const savePreferences = () => {
  const consent = {
    timestamp: Date.now(),
    preferences: preferences.value,
  };
  localStorage.setItem('gdpr-consent', JSON.stringify(consent));
  showGDPR.value = false;
  showDetails.value = false;

  // Emit event for analytics tracking
  window.dispatchEvent(new CustomEvent('gdpr-consent', { detail: consent }));
};

const toggleDetails = () => {
  showDetails.value = !showDetails.value;
};
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div
      v-if="showGDPR"
      class="fixed inset-0 z-50 flex items-end justify-end p-3 pointer-events-none"
    >
      <div
        class="w-full max-w-sm bg-white rounded-lg shadow-xl border border-black/10 pointer-events-auto"
      >
        <!-- Header -->
        <div class="p-4 pb-3">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Settings class="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 class="text-base font-semibold text-black/90">Cookie Settings</h3>
              <p class="text-xs text-black/50">Manage your preferences</p>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="px-4 pb-4">
          <p class="text-xs text-black/60 leading-relaxed mb-3">
            We use cookies to enhance your experience and analyze usage.
          </p>

          <!-- Cookie Details -->
          <Transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-72"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 max-h-72"
            leave-to-class="opacity-0 max-h-0"
          >
            <div v-if="showDetails" class="space-y-2 mb-3 overflow-hidden">
              <!-- Necessary Cookies -->
              <div class="flex items-center justify-between p-2 bg-black/5 rounded">
                <div class="flex-1">
                  <div class="flex items-center gap-1">
                    <h4 class="text-xs font-medium text-black/90">Necessary</h4>
                    <span class="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                      Required
                    </span>
                  </div>
                  <p class="text-xs text-black/50">Essential functionality</p>
                </div>
                <div class="w-8 h-4 bg-green-500 rounded-full flex items-center justify-end px-0.5">
                  <div class="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>

              <!-- Analytics Cookies -->
              <div class="flex items-center justify-between p-2 bg-black/5 rounded">
                <div class="flex-1">
                  <h4 class="text-xs font-medium text-black/90">Analytics</h4>
                  <p class="text-xs text-black/50">Usage insights</p>
                </div>
                <button
                  @click="preferences.analytics = !preferences.analytics"
                  :class="[
                    'w-8 h-4 rounded-full transition-colors duration-200 flex items-center px-0.5',
                    preferences.analytics ? 'bg-blue-500 justify-end' : 'bg-black/20 justify-start',
                  ]"
                >
                  <div
                    class="w-3 h-3 bg-white rounded-full transition-transform duration-200"
                  ></div>
                </button>
              </div>

              <!-- Marketing Cookies -->
              <div class="flex items-center justify-between p-2 bg-black/5 rounded">
                <div class="flex-1">
                  <h4 class="text-xs font-medium text-black/90">Marketing</h4>
                  <p class="text-xs text-black/50">Personalized content</p>
                </div>
                <button
                  @click="preferences.marketing = !preferences.marketing"
                  :class="[
                    'w-8 h-4 rounded-full transition-colors duration-200 flex items-center px-0.5',
                    preferences.marketing ? 'bg-blue-500 justify-end' : 'bg-black/20 justify-start',
                  ]"
                >
                  <div
                    class="w-3 h-3 bg-white rounded-full transition-transform duration-200"
                  ></div>
                </button>
              </div>

              <!-- Functional Cookies -->
              <div class="flex items-center justify-between p-2 bg-black/5 rounded">
                <div class="flex-1">
                  <h4 class="text-xs font-medium text-black/90">Functional</h4>
                  <p class="text-xs text-black/50">Save preferences</p>
                </div>
                <button
                  @click="preferences.functional = !preferences.functional"
                  :class="[
                    'w-8 h-4 rounded-full transition-colors duration-200 flex items-center px-0.5',
                    preferences.functional
                      ? 'bg-blue-500 justify-end'
                      : 'bg-black/20 justify-start',
                  ]"
                >
                  <div
                    class="w-3 h-3 bg-white rounded-full transition-transform duration-200"
                  ></div>
                </button>
              </div>
            </div>
          </Transition>

          <!-- Actions -->
          <div class="space-y-2">
            <!-- Toggle Details Button -->
            <button
              @click="toggleDetails"
              class="w-full text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              {{ showDetails ? 'Hide Details' : 'Customize' }}
            </button>

            <!-- Action Buttons -->
            <div class="flex gap-2">
              <Button variant="outline" class="flex-1 h-8 text-xs" @click="acceptNecessary">
                Essential Only
              </Button>
              <Button
                class="flex-1 h-8 text-xs bg-blue-600 hover:bg-blue-700"
                @click="showDetails ? savePreferences() : acceptAll()"
              >
                <Check class="w-3 h-3 mr-1" />
                {{ showDetails ? 'Save' : 'Accept All' }}
              </Button>
            </div>
          </div>

          <!-- Legal Links -->
          <div class="flex items-center justify-center gap-3 mt-3 pt-2 border-t border-black/5">
            <a href="/privacy" class="text-xs text-black/50 hover:text-black/70 transition-colors">
              Privacy
            </a>
            <a href="/cookies" class="text-xs text-black/50 hover:text-black/70 transition-colors">
              Cookies
            </a>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
